import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Settings, TrendingUp, TrendingDown, RefreshCw, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { loyaltyAPI } from '../../services/api';

interface LoyaltyTransaction {
  _id: string;
  customerId: { _id: string; firstName: string; lastName: string; email?: string } | null;
  orderId: { _id: string; orderNumber: string } | null;
  type: 'earn' | 'redeem' | 'adjust' | 'expire';
  points: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
}

interface LoyaltySettings {
  enabled: boolean;
  pointsPerPound: number;
  pointValue: number;
}

const LoyaltyManagement: React.FC = () => {
  const [settings, setSettings] = useState<LoyaltySettings>({ enabled: false, pointsPerPound: 10, pointValue: 0.01 });
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'settings' | 'transactions'>('settings');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, transactionsRes] = await Promise.all([
        loyaltyAPI.getSettings(),
        loyaltyAPI.getTransactions(),
      ]);
      setSettings(settingsRes.data);
      setTransactions(transactionsRes.data.transactions || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load loyalty data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await loyaltyAPI.updateSettings(settings);
      toast.success('Loyalty settings saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const typeColors: Record<string, string> = {
    earn: 'bg-green-100 text-green-800',
    redeem: 'bg-red-100 text-red-800',
    adjust: 'bg-yellow-100 text-yellow-800',
    expire: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Star className="h-6 w-6 text-yellow-500" /> Loyalty Points
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage your loyalty program settings and view transaction history</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'settings' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Settings className="h-4 w-4 inline mr-1.5" />Settings
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'transactions' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <RefreshCw className="h-4 w-4 inline mr-1.5" />Transactions
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : activeTab === 'settings' ? (
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings(s => ({ ...s, enabled: e.target.checked }))}
                className="rounded text-primary-600 focus:ring-primary-500 h-5 w-5"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Enable Loyalty Program</span>
                <p className="text-xs text-gray-500">Customers will earn and redeem points on orders</p>
              </div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Points Earned Per £1 Spent</label>
              <input
                type="number"
                min={0}
                value={settings.pointsPerPound}
                onChange={(e) => setSettings(s => ({ ...s, pointsPerPound: parseInt(e.target.value) || 0 }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">e.g. 10 means a £5 order earns 50 points</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Point Value (£)</label>
              <input
                type="number"
                step="0.001"
                min={0}
                value={settings.pointValue}
                onChange={(e) => setSettings(s => ({ ...s, pointValue: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">e.g. 0.01 means 100 points = £1.00 off</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
            <strong>Example:</strong> With {settings.pointsPerPound} pts/£ and £{settings.pointValue} per point, a £10.00 order earns <strong>{settings.pointsPerPound * 10} points</strong> (worth <strong>£{(settings.pointsPerPound * 10 * settings.pointValue).toFixed(2)}</strong>).
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Points</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance After</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">No transactions yet</td></tr>
              ) : transactions.map((t) => (
                <tr key={t._id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {t.customerId ? `${t.customerId.firstName} ${t.customerId.lastName}` : '—'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[t.type] || 'bg-gray-100 text-gray-800'}`}>
                      {t.type === 'earn' && <TrendingUp className="h-3 w-3" />}
                      {t.type === 'redeem' && <TrendingDown className="h-3 w-3" />}
                      {t.type}
                    </span>
                  </td>
                  <td className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${t.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {t.points > 0 ? '+' : ''}{t.points}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{t.balanceAfter}</td>
                  <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {t.orderId ? t.orderId.orderNumber : '—'}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">{t.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LoyaltyManagement;
